import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Skeleton, Table, Tag } from "antd";
import { CloseOutlined, DeleteOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import swal from "sweetalert";
import assistantIcon from "../../assets/gifs/Ai Robot Vector Art.gif";
import RAG_API from "../../apis/rag.api";
import { ragModuleOptions } from "../../components/SidebarConstants";
import Theor from "../../components/shared/Theor";
import "./AIRAG.css";

const DEFAULT_MODULE = "advisor-panel";
const DEFAULT_CHAT_TOP_K = 5;
const DEFAULT_DOCUMENT_LIMIT = 10;
const INITIAL_CHAT_MESSAGE = {
  role: "assistant",
  text: "Hi there. How can I help you today?",
  state: "idle",
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const pickArrayFromResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload.results,
    payload.documents,
    payload.matches,
    payload.items,
    payload.data,
    payload.hits,
    payload.output,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  if (isPlainObject(payload.data)) {
    return pickArrayFromResponse(payload.data);
  }

  return [];
};

const pickObjectFromResponse = (payload) => {
  if (!payload || !isPlainObject(payload)) return null;

  const candidates = [
    payload.document,
    payload.item,
    payload.data,
    payload.result,
    payload.output,
  ];

  for (const candidate of candidates) {
    if (isPlainObject(candidate)) {
      return candidate;
    }
  }

  return payload;
};

const getDocumentId = (document) =>
  (document && document.id) ||
  (document && document._id) ||
  (document && document.documentId) ||
  (document && document.document_id) ||
  (document && document.uuid) ||
  "";

const getDocumentTitle = (document, index = 0) =>
  (document && document.name) ||
  (document && document.title) ||
  (document && document.document_name) ||
  (document && document.doc_name) ||
  `Document ${index + 1}`;

const getDocumentSourceType = (document) =>
  (document && document.source_type) || (document && document.sourceType) || "";

const getDocumentStatus = (document) => (document && document.status) || "";

const getDocumentTextContent = (document) =>
  (document && document.text_content) ||
  (document && document.textContent) ||
  (document && document.content) ||
  (document && document.body) ||
  "";

const formatDocumentDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDocumentDetail = (document) => {
  if (!document) return "";
  if (typeof document === "string") return document;

  try {
    return JSON.stringify(document, null, 2);
  } catch (error) {
    return String(document);
  }
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "object") {
    return formatDocumentDetail(value);
  }

  return String(value);
};

const getDocumentDetailRows = (document) => {
  if (!document || typeof document !== "object") {
    return [];
  }

  const rows = [
    { label: "Title", value: getDocumentTitle(document) },
    { label: "Module", value: document.module },
    { label: "Status", value: document.status },
    { label: "Source type", value: getDocumentSourceType(document) },
    // { label: "Created at", value: formatDocumentDate(document.created_at || document.createdAt) },
    // { label: "Updated at", value: formatDocumentDate(document.updated_at || document.updatedAt) },
  ];

  return rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== "");
};

const getReadableDocumentText = (document) => {
  const text = getDocumentTextContent(document);

  if (text === null || text === undefined || text === "") {
    return "";
  }

  if (typeof text === "string") {
    return text.trim();
  }

  return formatDisplayValue(text).trim();
};

const renderDocumentTableSkeleton = () => (
  <div className="airag-table-skeleton" aria-busy="true" aria-live="polite">
    <div className="airag-table-skeleton__header">
      <Skeleton active title={{ width: "38%" }} paragraph={false} />
    </div>

    {Array.from({ length: 5 }).map((_, index) => (
      <div className="airag-table-skeleton__row" key={`airag-table-skeleton-${index}`}>
        <div className="airag-table-skeleton__cell airag-table-skeleton__cell--title">
          <Skeleton active title={false} paragraph={{ rows: 2, width: ["82%", "54%"] }} />
        </div>
        <div className="airag-table-skeleton__cell">
          <Skeleton active title={false} paragraph={{ rows: 1, width: "70%" }} />
        </div>
        <div className="airag-table-skeleton__cell">
          <Skeleton active title={false} paragraph={{ rows: 1, width: "62%" }} />
        </div>
        <div className="airag-table-skeleton__cell">
          <Skeleton active title={false} paragraph={{ rows: 1, width: "58%" }} />
        </div>
        <div className="airag-table-skeleton__cell">
          <Skeleton active title={false} paragraph={{ rows: 1, width: "74%" }} />
        </div>
        <div className="airag-table-skeleton__cell airag-table-skeleton__cell--actions">
          <Skeleton active title={false} paragraph={{ rows: 1, width: "100%" }} />
        </div>
      </div>
    ))}
  </div>
);

const renderDocumentModalSkeleton = () => (
  <div className="airag-document-detail__skeleton" aria-busy="true" aria-live="polite">
    <Skeleton active title={{ width: "48%" }} paragraph={{ rows: 1, width: ["88%"] }} />
    <div className="airag-document-detail__grid">
      {Array.from({ length: 6 }).map((_, index) => (
        <div className="airag-document-detail__item" key={`airag-detail-skeleton-${index}`}>
          <Skeleton active title={{ width: "44%" }} paragraph={{ rows: 1, width: ["78%"] }} />
        </div>
      ))}
    </div>
    <div className="airag-document-detail__content">
      <div className="airag-document-detail__label">Text content</div>
      <Skeleton active title={false} paragraph={{ rows: 5, width: ["100%", "96%", "92%", "84%", "70%"] }} />
    </div>
  </div>
);

const buildCorsFallbackResponse = (action, payload) => ({
  action,
  ui_notice:
    "The request was sent successfully and the response is still coming. We will show the live result here as soon as it arrives.",
  request: payload,
});

const getStreamContent = (parsedPayload) => {
  if (!parsedPayload) return "";
  if (typeof parsedPayload === "string") return parsedPayload;
  if (parsedPayload.content) return parsedPayload.content;
  if (parsedPayload.text) return parsedPayload.text;
  if (parsedPayload.answer) return parsedPayload.answer;
  if (parsedPayload.message && parsedPayload.message.content) {
    return parsedPayload.message.content;
  }
  if (
    parsedPayload.choices &&
    parsedPayload.choices[0] &&
    parsedPayload.choices[0].delta &&
    parsedPayload.choices[0].delta.content
  ) {
    return parsedPayload.choices[0].delta.content;
  }

  return "";
};

const normalizeStreamText = (rawText) => {
  if (!rawText) return "";
  if (rawText.indexOf("data:") === -1) return rawText;

  const lines = rawText.split(/\r?\n/);
  let output = "";
  let sawDataLine = false;

  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (!trimmed) continue;

    if (trimmed === "data: [DONE]") {
      sawDataLine = true;
      continue;
    }

    if (trimmed.indexOf("data:") === 0) {
      sawDataLine = true;
      const payloadText = trimmed.replace(/^data:\s*/, "");

      if (!payloadText) {
        continue;
      }

      try {
        const parsed = JSON.parse(payloadText);
        const content = getStreamContent(parsed);
        if (content) {
          output += content;
        }
      } catch (error) {
        output += payloadText;
      }
    }
  }

  return sawDataLine ? output : rawText;
};

const replaceLastChatMessage = (messages, nextText, nextState) => {
  if (!messages.length) return messages;

  const nextMessages = messages.slice();
  const lastIndex = nextMessages.length - 1;
  const lastMessage = nextMessages[lastIndex];

  nextMessages[lastIndex] = {
    ...lastMessage,
    text: nextText,
    streaming: nextState === "streaming",
    state: nextState,
  };

  return nextMessages;
};

const AIRAG = ({ showWorkspace = true }) => {
  const [ingestForm, setIngestForm] = useState({
    name: "",
    text: "",
    module: DEFAULT_MODULE,
  });
  const [searchForm, setSearchForm] = useState({
    query: "",
    module: DEFAULT_MODULE,
    top_k: DEFAULT_CHAT_TOP_K,
  });
  const [activeTab, setActiveTab] = useState("documents");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseState, setResponseState] = useState(null);
  const [errorState, setErrorState] = useState("");
  const [documentListState, setDocumentListState] = useState({
    items: [],
    total: 0,
  });
  const [documentFilters, setDocumentFilters] = useState({
    module: "",
    page: 1,
    limit: DEFAULT_DOCUMENT_LIMIT,
  });
  const [isDocumentListLoading, setIsDocumentListLoading] = useState(false);
  const [documentListError, setDocumentListError] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentError, setSelectedDocumentError] = useState("");
  const [isSelectedDocumentLoading, setIsSelectedDocumentLoading] = useState(false);
  const [documentReloadToken, setDocumentReloadToken] = useState(0);
  const [isIngestModalOpen, setIsIngestModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [chatForm, setChatForm] = useState({
    query: "",
  });
  const [chatMessages, setChatMessages] = useState([INITIAL_CHAT_MESSAGE]);
  const [chatErrorState, setChatErrorState] = useState("");
  const [isChatSubmitting, setIsChatSubmitting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const chatEndRef = useRef(null);
  const chatSessionRef = useRef(0);

  const groupedModuleOptions = useMemo(() => {
    const groups = {};

    ragModuleOptions.forEach((option) => {
      const groupName = option.group || "Modules";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(option);
    });

    return groups;
  }, []);

  useEffect(() => {
    if (chatEndRef.current && chatEndRef.current.scrollIntoView) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chatMessages, isChatSubmitting]);

  useEffect(() => {
    if (activeTab !== "documents") {
      return undefined;
    }

    let cancelled = false;

    const loadDocuments = async () => {
      setIsDocumentListLoading(true);
      setDocumentListError("");

      try {
        const response = await RAG_API.listDocumentsRAG({
          page: documentFilters.page,
          limit: documentFilters.limit,
          ...(documentFilters.module.trim() ? { module: documentFilters.module.trim() } : {}),
        });

        if (cancelled) {
          return;
        }

        const items = pickArrayFromResponse(response);
        const total =
          (response && response.total) ||
          (response && response.count) ||
          (response && response.totalCount) ||
          (response && response.data && response.data.total) ||
          (response && response.data && response.data.count) ||
          items.length;

        setDocumentListState({
          items,
          total,
        });

      } catch (error) {
        if (!cancelled) {
          setDocumentListError("Could not load documents right now.");
        }
      } finally {
        if (!cancelled) {
          setIsDocumentListLoading(false);
        }
      }
    };

    loadDocuments();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    documentFilters.page,
    documentFilters.limit,
    documentFilters.module,
    documentReloadToken,
  ]);

  const responseData = responseState || {};
  const responseItems = pickArrayFromResponse(responseData);
  const isCorsResponse = Boolean(responseData.ui_notice);
  const isIngestResponse = responseData.count !== undefined || responseData.items !== undefined;
  const isSearchResponse = responseData.results !== undefined;
  const hasResponse = Boolean(responseState);
  const responseHeading = isIngestResponse
    ? "Document added"
    : isCorsResponse
    ? "Response in progress"
    : isSearchResponse
    ? "Search results"
    : "Recent activity";
  const responseSubheading = isIngestResponse
    ? "Your document was ingested successfully."
    : isCorsResponse
    ? responseData.ui_notice
    : isSearchResponse
    ? "Here are the best matches from your knowledge base."
    : "Use the workspace above to add documents or search your knowledge base.";

  const handleDocumentSelect = async (document) => {
    const documentId = getDocumentId(document);
    if (!documentId) {
      setSelectedDocumentError("This document does not have a usable id.");
      return;
    }

    setSelectedDocumentId(documentId);
    setSelectedDocument(document);
    setSelectedDocumentError("");
    setIsSelectedDocumentLoading(true);
    setIsDocumentModalOpen(true);

    try {
      const response = await RAG_API.getDocumentRAG(documentId);
      const nextDocument = pickObjectFromResponse(response) || document;
      setSelectedDocument(nextDocument);
    } catch (error) {
      setSelectedDocumentError("Could not load document details.");
    } finally {
      setIsSelectedDocumentLoading(false);
    }
  };

  const handleDocumentDelete = async (document) => {
    const documentId = getDocumentId(document);
    if (!documentId) {
      setDocumentListError("This document does not have a usable id.");
      return;
    }

    const confirmed = await swal({
      title: "Delete document?",
      text: "This will permanently remove the selected document from RAG.",
      dangerMode: true,
      buttons: ["No, cancel it!", "Yes, delete it!"],
      icon: "warning",
      type: "warning",
      closeOnConfirm: false,
      closeOnCancel: false,
    });

    if (!confirmed) {
      swal("Cancelled", "The document was not deleted.", "error");
      return;
    }

    setDocumentListError("");

    try {
      await RAG_API.deleteDocumentRAG(documentId);
      setDocumentListState((current) => ({
        ...current,
        items: current.items.filter((item) => getDocumentId(item) !== documentId),
      }));
      refreshDocuments();

      if (selectedDocumentId === documentId) {
        closeDocumentModal();
      }

      swal("Deleted!", "Your document has been deleted.", "success");
    } catch (error) {
      setDocumentListError("Could not delete the document right now.");
      swal("Error", "Could not delete the document right now.", "error");
    }
  };

  const refreshDocuments = () => {
    setDocumentReloadToken((current) => current + 1);
  };

  const openIngestModal = () => {
    setErrorState("");
    setIsIngestModalOpen(true);
  };

  const closeIngestModal = () => {
    setIsIngestModalOpen(false);
  };

  const closeDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
    setSelectedDocumentId("");
    setSelectedDocumentError("");
    setIsSelectedDocumentLoading(false);
  };

  const runIngest = async () => {
    setErrorState("");
    setIsSubmitting(true);

    const payload = {
      documents: [
        {
          name: ingestForm.name.trim(),
          text: ingestForm.text.trim(),
          module: ingestForm.module.trim(),
        },
      ],
    };

    try {
      const response = await RAG_API.ingestDocumentRAG(payload);
      if (!response) {
        setErrorState("The document was sent, but no response was returned yet. Please try again in a moment.");
        return;
      }

      setResponseState(response);
      setIsIngestModalOpen(false);
      setActiveTab("documents");
      setIngestForm((current) => ({
        ...current,
        name: "",
        text: "",
      }));
      refreshDocuments();
    } catch (error) {
      setErrorState("Could not inject the document right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const runSearch = async () => {
    setErrorState("");
    setIsSubmitting(true);

    const payload = {
      query: searchForm.query.trim(),
      module: searchForm.module.trim(),
      top_k: Number(searchForm.top_k) || 5,
    };

    setSearchForm((current) => ({
      ...current,
      query: "",
    }));

    try {
      const response = await RAG_API.searchDocumentsRAG(payload);
      console.log("Received search response:", response);
      if (!response) {
        setResponseState(buildCorsFallbackResponse("search", payload));
        return;
      }

      setResponseState(response);
    } catch (error) {
      setResponseState(buildCorsFallbackResponse("search", payload));
      setErrorState("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleChat = () => {
    setIsChatOpen((current) => !current);
    setChatErrorState("");
  };

  const runChat = async () => {
    const sessionId = chatSessionRef.current + 1;
    chatSessionRef.current = sessionId;

    const nextQuery = chatForm.query.trim();
    if (!nextQuery) {
      setChatErrorState("Please enter a question first.");
      return;
    }

    setChatErrorState("");
    setIsChatSubmitting(true);
    setIsChatOpen(true);
    setChatForm((current) => ({
      ...current,
      query: "",
    }));

    const userMessage = {
      role: "user",
      text: nextQuery,
      state: "done",
    };
    const assistantMessage = {
      role: "assistant",
      text: "",
      state: "streaming",
      streaming: true,
    };

    setChatMessages((current) => current.concat([userMessage, assistantMessage]));

    try {
      const payload = {
        query: nextQuery,
        top_k: DEFAULT_CHAT_TOP_K,
      };
      const response = await RAG_API.streamSearchDocumentsRAG(payload);
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      if (!response || !response.ok) {
        throw new Error("CORS blocked the chat response.");
      }

      if (!response.body || !response.body.getReader) {
        throw new Error("This browser does not support streamed responses.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let rawText = "";
      let done = false;

      while (!done) {
        const result = await reader.read();
        const value = result.value;
        done = result.done;

        if (value) {
          rawText += decoder.decode(value, { stream: true });
          const formattedText = normalizeStreamText(rawText);

          if (chatSessionRef.current !== sessionId) {
            return;
          }

          setChatMessages((current) => {
            if (!current.length) return current;
            return replaceLastChatMessage(current, formattedText, "streaming");
          });
        }
      }

      const trailingText = decoder.decode();
      if (trailingText) {
        rawText += trailingText;
      }

      const finalText = normalizeStreamText(rawText).trim();
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      setChatMessages((current) => {
        if (!current.length) return current;
        return replaceLastChatMessage(
          current,
          finalText || "No answer was returned from the stream.",
          "done"
        );
      });
    } catch (error) {
      if (chatSessionRef.current !== sessionId) {
        return;
      }
      setChatErrorState(
        "The request was sent, but the browser could not read the response because of CORS. We will enable the live chat once the backend allows this origin."
      );
      setChatMessages((current) => {
        if (!current.length) return current;
        return replaceLastChatMessage(
          current,
          "I sent your message, but the browser is blocking the live response for now. We will show the answer once CORS is enabled.",
          "error"
        );
      });
    } finally {
      if (chatSessionRef.current === sessionId) {
        setIsChatSubmitting(false);
      }
    }
  };

  const chatWidget = (
    <>
      <button
        type="button"
        className={`airag-chat-launcher${isChatOpen ? " is-open" : ""}`}
        onClick={toggleChat}
        aria-label={isChatOpen ? "Close Assetra AI" : "Open Assetra AI"}
      >
        <span className="airag-chat-launcher__glow" />
        <span className="airag-chat-launcher__icon">
          <img src={assistantIcon} alt="Assetra AI" />
        </span>
        <span className="airag-chat-launcher__copy">
          <strong>Assetra AI</strong>
          <span>{isChatOpen ? "Tap to minimize" : "Tap to open"}</span>
        </span>
        {!isChatOpen ? <span className="airag-chat-launcher__badge" /> : null}
      </button>

      <section className={`airag-chat-panel${isChatOpen ? " is-open" : ""}`}>
        <div className="airag-chat-panel__header">
          <div className="airag-chat-panel__title-wrap">
            <div className="airag-chat-panel__eyebrow">Asset Planet assistant</div>
            <h2 className="airag-chat-panel__title">Ask AI about Asset Planet</h2>
            <div className="airag-chat-panel__subtitle">
              Ask naturally about your account, plans, tasks, and saved information.
            </div>
          </div>
          <button
            type="button"
            className="airag-chat-panel__close"
            onClick={toggleChat}
            aria-label="Close AI RAG chat"
            title="Close"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="airag-chat__thread">
          {chatMessages.map((message, index) => (
            <div
              className={`airag-chat__message${
                message.role === "user" ? " is-user" : " is-assistant"
              }${message.state === "streaming" ? " is-streaming" : ""}`}
              key={`${message.role}-${index}`}
            >
              <div className="airag-chat__message-meta">
                {message.role === "user" ? "You" : "Asset Planet AI"}
              </div>
              <div className="airag-chat__message-text">
                {message.role === "assistant" ? (
                  <Theor className="airag-chat__markdown">
                    {message.text || (message.state === "streaming" ? "Streaming response..." : "")}
                  </Theor>
                ) : (
                  message.text || ""
                )}
              </div>
            </div>
          ))}
          {isChatSubmitting ? (
            <div className="airag-chat__typing">Streaming answer from the RAG endpoint...</div>
          ) : null}
          <div ref={chatEndRef} />
        </div>

        {chatErrorState ? <div className="airag-alert">{chatErrorState}</div> : null}

        <form
          className="airag-chat__composer"
          onSubmit={(event) => {
            event.preventDefault();
            runChat();
          }}
        >
          {/*
          <div className="airag-field">
            <label className="airag-label" htmlFor="rag-chat-module">
              Module
            </label>
            <select
              id="rag-chat-module"
              className="airag-input"
              value={chatForm.module}
              onChange={(event) => {
                const value = event.target.value;
                setChatForm((current) => ({
                  ...current,
                  module: value,
                }));
              }}
            >
              {Object.keys(groupedModuleOptions).map((groupName) => (
                <optgroup key={groupName} label={groupName}>
                  {groupedModuleOptions[groupName].map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          */}

          <div className="airag-field">
            <label className="airag-label" htmlFor="rag-chat-query">
              Your question
            </label>
            <input
              id="rag-chat-query"
              type="text"
              className="airag-input airag-chat__input"
              value={chatForm.query}
              onChange={(event) => {
                const value = event.target.value;
                setChatForm((current) => ({
                  ...current,
                  query: value,
                }));
              }}
              placeholder="Ask a question about Asset Planet"
            />
          </div>

          <div className="airag-actions airag-chat__actions">
            <button
              type="submit"
              className="airag-button airag-button--primary"
              disabled={isChatSubmitting || !chatForm.query.trim()}
            >
              {isChatSubmitting ? "Thinking..." : "Ask AI"}
            </button>
          </div>
        </form>
      </section>
    </>
  );

  return showWorkspace ? (
    <div className="airag-page">
      <section className="airag-shell">
        <div className="airag-layout">
          <section className="airag-panel airag-panel--form">
            <div className="airag-panel__header">
              <div>
                <h2 className="airag-panel__title">
                  {activeTab === "search" ? "Document search" : "Document library"}
                </h2>
              </div>
              <div className="airag-panel__toggle">
                <button
                  type="button"
                  className={`airag-toggle${activeTab === "search" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("search")}
                >
                  Search
                </button>
                <button
                  type="button"
                  className={`airag-toggle${activeTab === "documents" ? " is-active" : ""}`}
                  onClick={() => setActiveTab("documents")}
                >
                  Documents
                </button>
              </div>
            </div>

            {activeTab === "search" ? (
              <div className="airag-form">
                <div className="airag-field">
                  <label className="airag-label" htmlFor="rag-query">
                    Query
                  </label>
                  <input
                    id="rag-query"
                    className="airag-input"
                    value={searchForm.query}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSearchForm((current) => ({
                        ...current,
                        query: value,
                      }));
                    }}
                    placeholder="Search your documents"
                  />
                </div>

                <div className="airag-row">
                  <div className="airag-field">
                    <label className="airag-label" htmlFor="rag-search-module">
                      Module
                    </label>
                    <select
                      id="rag-search-module"
                      className="airag-input"
                      value={searchForm.module}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchForm((current) => ({
                          ...current,
                          module: value,
                        }));
                      }}
                    >
                      {Object.keys(groupedModuleOptions).map((groupName) => (
                        <optgroup key={groupName} label={groupName}>
                          {groupedModuleOptions[groupName].map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="airag-field">
                    <label className="airag-label" htmlFor="rag-top-k">
                      Top K
                    </label>
                    <input
                      id="rag-top-k"
                      type="number"
                      min="1"
                      max="20"
                      className="airag-input"
                      value={searchForm.top_k}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchForm((current) => ({
                          ...current,
                          top_k: value,
                        }));
                      }}
                    />
                  </div>
                </div>

                <div className="airag-actions">
                  <button
                    type="button"
                    className="airag-button airag-button--primary"
                    onClick={runSearch}
                    disabled={isSubmitting || !searchForm.query.trim()}
                  >
                    {isSubmitting ? "Searching..." : "Search documents"}
                  </button>
                </div>
              </div>
            ) : null}
          </section>

        </div>

        <section className="airag-panel airag-panel--response">
          <div className="airag-panel__header">
            <div>
              <div className="airag-panel__kicker">Activity</div>
              <h2 className="airag-panel__title">
                {activeTab === "documents" ? "Document library" : responseHeading}
              </h2>
            </div>
          </div>

          {activeTab === "documents" ? (
            <>
              {documentListError ? <div className="airag-alert">{documentListError}</div> : null}

              {isDocumentListLoading ? (
                renderDocumentTableSkeleton()
              ) : documentListState.items.length > 0 ? (
                <>
                  <div className="airag-library-toolbar" style={{ marginBottom: 12 }}>
                    <div className="airag-field" style={{ minWidth: 260, marginBottom: 0 }}>
                      <label className="airag-label" htmlFor="rag-documents-module">
                        Filter by module
                      </label>
                      <select
                        id="rag-documents-module"
                        className="airag-input"
                        value={documentFilters.module}
                        onChange={(event) => {
                          const value = event.target.value;
                          setDocumentFilters((current) => ({
                            ...current,
                            module: value,
                            page: 1,
                          }));
                        }}
                      >
                        <option value="">All modules</option>
                        {Object.keys(groupedModuleOptions).map((groupName) => (
                          <optgroup key={groupName} label={groupName}>
                            {groupedModuleOptions[groupName].map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className="airag-button airag-button--primary"
                      onClick={openIngestModal}
                    >
                      <PlusOutlined />
                      <span>Inject document</span>
                    </button>
                  </div>

                  <Table
                    rowKey={(record, index) => getDocumentId(record) || `document-${index}`}
                    dataSource={documentListState.items}
                    loading={false}
                    pagination={{
                      current: documentFilters.page,
                      pageSize: documentFilters.limit,
                      total: documentListState.total || documentListState.items.length,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    onChange={(pagination) => {
                      setDocumentFilters((current) => ({
                        ...current,
                        page: pagination.current || 1,
                        limit: pagination.pageSize || DEFAULT_DOCUMENT_LIMIT,
                      }));
                    }}
                    onRow={(record) => ({
                      onClick: () => handleDocumentSelect(record),
                    })}
                    columns={[
                      {
                        title: "Document",
                        key: "document",
                        render: (_, record, index) => {
                          const title = getDocumentTitle(record, index);
                          const documentId = getDocumentId(record);
                          return (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: "#101828", fontWeight: 700 }}>{title}</div>
                              <div style={{ color: "#667085", fontSize: 12, marginTop: 4 }}>
                                {documentId ? `ID: ${documentId}` : "No document id available"}
                              </div>
                            </div>
                          );
                        },
                      },
                      {
                        title: "Module",
                        key: "module",
                        render: (_, record) => {
                          const moduleName = record && record.module ? record.module : "Unassigned";
                          return <Tag color="blue">{moduleName}</Tag>;
                        },
                      },
                      {
                        title: "Status",
                        key: "status",
                        render: (_, record) => {
                          const status = getDocumentStatus(record) || "Unknown";
                          const normalized = String(status).toLowerCase();
                          const color =
                            normalized === "ingested"
                              ? "green"
                              : normalized === "failed" || normalized === "error"
                              ? "red"
                              : "gold";
                          return <Tag color={color}>{status}</Tag>;
                        },
                      },
                      {
                        title: "Source type",
                        key: "sourceType",
                        render: (_, record) => getDocumentSourceType(record) || "—",
                      },
                      {
                        title: "Updated",
                        key: "updated",
                        render: (_, record) =>
                          formatDocumentDate(record.updated_at || record.updatedAt) || "—",
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <div className="airag-table__actions">
                            <button
                              type="button"
                              className="airag-link-button airag-table__action"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDocumentSelect(record);
                              }}
                            >
                              <EyeOutlined />
                              <span>View</span>
                            </button>
                            <button
                              type="button"
                              className="airag-link-button airag-table__action airag-table__action--danger"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDocumentDelete(record);
                              }}
                            >
                              <DeleteOutlined />
                              <span>Delete</span>
                            </button>
                          </div>
                        ),
                      },
                    ]}
                    locale={{
                      emptyText: "No documents were returned for the current filters.",
                    }}
                  />
                </>
              ) : (
                <div className="airag-empty-state">
                  No documents were returned for the current filters.
                </div>
              )}
            </>
          ) : (
            <>
              {errorState ? <div className="airag-alert">{errorState}</div> : null}

              <div className="airag-response-summary">
                <div className="airag-response-summary__content">
                  <div className="airag-response-summary__subhead">{responseSubheading}</div>
                  {isCorsResponse ? (
                    <div className="airag-response-summary__notice">
                      <div className="airag-response-summary__notice-title">
                        Response is still coming
                      </div>
                      <div className="airag-response-summary__notice-body">
                        The request went through. We are still waiting for the API response, and
                        this screen will update as soon as it arrives.
                      </div>
                    </div>
                  ) : null}
                  {isIngestResponse ? (
                    <div className="airag-response-summary__stats">
                      <div className="airag-response-summary__stat">
                        <span>Documents</span>
                        <strong>{responseData.count || responseItems.length || 0}</strong>
                      </div>
                      <div className="airag-response-summary__stat">
                        <span>Status</span>
                        <strong>
                          {responseItems[0] && responseItems[0].status
                            ? responseItems[0].status
                            : "Ready"}
                        </strong>
                      </div>
                    </div>
                  ) : null}
                  {isSearchResponse && responseItems.length > 0 ? (
                    <div className="airag-response-summary__stats">
                      <div className="airag-response-summary__stat">
                        <span>Matches</span>
                        <strong>{responseItems.length}</strong>
                      </div>
                      <div className="airag-response-summary__stat">
                        <span>Top score</span>
                        <strong>
                          {responseItems[0] && responseItems[0].score !== undefined
                            ? responseItems[0].score.toFixed
                              ? responseItems[0].score.toFixed(3)
                              : responseItems[0].score
                            : "Ready"}
                        </strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {responseItems.length > 0 ? (
                <div className="airag-results-grid">
                  {responseItems.map((item, index) => {
                    const title =
                      (item && item.name) ||
                      (item && item.title) ||
                      (item && item.document_name) ||
                      (item && item.doc_name) ||
                      `Item ${index + 1}`;
                    const body =
                      (item && item.text) ||
                      (item && item.content) ||
                      (item && item.snippet) ||
                      (item && item.chunk) ||
                      (item && item.description) ||
                      (item && item.status) ||
                      "";
                    const hasScore =
                      item && (item.score !== undefined || item.similarity !== undefined);
                    const scoreValue = item
                      ? item.score !== undefined
                        ? item.score
                        : item.similarity
                      : undefined;
                    const badgeLabel =
                      item && item.module
                        ? item.module
                        : item && item.updated_at
                        ? item.updated_at
                        : "";

                    return (
                      <article className="airag-result-card" key={`${title}-${index}`}>
                        <div className="airag-result-card__top">
                          <h3 className="airag-result-card__title">{title}</h3>
                          {hasScore ? (
                            <span className="airag-result-card__score">{scoreValue}</span>
                          ) : null}
                        </div>

                        {badgeLabel ? (
                          <div className="airag-result-card__badge">{badgeLabel}</div>
                        ) : null}
                        <p className="airag-result-card__body">{body}</p>
                      </article>
                    );
                  })}
                </div>
              ) : hasResponse ? (
                <div className="airag-empty-state">
                  We received your request. Add another document or run a search to continue.
                </div>
              ) : null}
            </>
          )}
        </section>

        <Modal
          visible={isIngestModalOpen}
          onCancel={() => {
            if (!isSubmitting) {
              closeIngestModal();
            }
          }}
          footer={null}
          centered
          width={680}
          destroyOnClose
          className="airag-modal airag-modal--wide"
          maskClosable={!isSubmitting}
          closable={!isSubmitting}
        >
          <div className="airag-modal__header">
            <h3 className="airag-modal__title">Inject a new document into RAG</h3>
          </div>

          <div className="airag-form">
            <div className="airag-field">
              <label className="airag-label" htmlFor="rag-document-name">
                Document name
              </label>
              <input
                id="rag-document-name"
                className="airag-input"
                value={ingestForm.name}
                onChange={(event) => {
                  const value = event.target.value;
                  setIngestForm((current) => ({
                    ...current,
                    name: value,
                  }));
                }}
                placeholder="Enter document name"
              />
            </div>

            <div className="airag-field">
              <label className="airag-label" htmlFor="rag-document-module">
                Module
              </label>
              <select
                id="rag-document-module"
                className="airag-input"
                value={ingestForm.module}
                onChange={(event) => {
                  const value = event.target.value;
                  setIngestForm((current) => ({
                    ...current,
                    module: value,
                  }));
                }}
              >
                {Object.keys(groupedModuleOptions).map((groupName) => (
                  <optgroup key={groupName} label={groupName}>
                    {groupedModuleOptions[groupName].map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="airag-field">
              <label className="airag-label" htmlFor="rag-document-text">
                Document text
              </label>
              <textarea
                id="rag-document-text"
                className="airag-textarea"
                rows={10}
                value={ingestForm.text}
                onChange={(event) => {
                  const value = event.target.value;
                  setIngestForm((current) => ({
                    ...current,
                    text: value,
                  }));
                }}
                placeholder="Paste or type document content here"
              />
            </div>

            <div className="airag-actions">
              <button
                type="button"
                className="airag-button airag-button--ghost"
                onClick={closeIngestModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="airag-button airag-button--primary"
                onClick={runIngest}
                disabled={isSubmitting || !ingestForm.name.trim() || !ingestForm.text.trim()}
              >
                {isSubmitting ? "Injecting..." : "Inject document"}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          visible={isDocumentModalOpen}
          onCancel={closeDocumentModal}
          footer={null}
          centered
          width={720}
          destroyOnClose
          className="airag-modal airag-modal--details"
        >
          <div className="airag-modal__header">
            <h3 className="airag-modal__title">
              {selectedDocument ? getDocumentTitle(selectedDocument) : "Document details"}
            </h3>
          </div>

          {selectedDocumentError ? <div className="airag-alert">{selectedDocumentError}</div> : null}
          {isSelectedDocumentLoading ? (
            renderDocumentModalSkeleton()
          ) : selectedDocument ? (
            <>
              <div className="airag-document-detail__grid">
                {getDocumentDetailRows(selectedDocument).map((row) => (
                  <div className="airag-document-detail__item" key={row.label}>
                    <span className="airag-document-detail__label">{row.label}</span>
                    <span className="airag-document-detail__value">
                      {formatDisplayValue(row.value)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="airag-document-detail__content">
                <div className="airag-document-detail__label">Text content</div>
                <div className="airag-document-detail__content-body">
                  {getReadableDocumentText(selectedDocument) ? (
                    <Theor>{getReadableDocumentText(selectedDocument)}</Theor>
                  ) : (
                    <span className="airag-document-detail__empty">No text content available.</span>
                  )}
                </div>
              </div>

              <div className="airag-actions airag-actions--left airag-modal__actions">
                <button
                  type="button"
                  className="airag-button airag-button--ghost"
                  onClick={() => handleDocumentDelete(selectedDocument)}
                >
                  <span className="airag-modal__delete-icon">
                    <DeleteOutlined />
                    <span>Delete document</span>
                  </span>
                </button>
              </div>
            </>
          ) : (
            <div className="airag-empty-state">Pick a document from the table to inspect it.</div>
          )}
        </Modal>

        {chatWidget}
      </section>
    </div>
  ) : chatWidget;
};

export { AIRAG };
export default AIRAG;
